/** Anthropic vision accepts only these image media types. */
const VISION_TYPES = new Set([
	"image/jpeg",
	"image/jpg",
	"image/png",
	"image/gif",
	"image/webp",
]);

export type ChatFilePart = {
	mediaType: string;
	url: string;
	filename?: string;
};

function mediaTypeFromDataUrl(url: string): string | null {
	const match = /^data:([^;,]+)/i.exec(url);
	return match?.[1]?.toLowerCase() ?? null;
}

function isSvg(mediaType: string, filename?: string): boolean {
	const type = mediaType.toLowerCase();
	if (type === "image/svg+xml" || type.includes("svg")) return true;
	return Boolean(filename?.toLowerCase().endsWith(".svg"));
}

/**
 * Rasterize SVG data URL → PNG for Anthropic vision (SVG is not a valid image type).
 */
async function svgDataUrlToPng(dataUrl: string): Promise<string | null> {
	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			try {
				const width = Math.max(1, Math.min(img.naturalWidth || 512, 1024));
				const height = Math.max(1, Math.min(img.naturalHeight || 512, 1024));
				const canvas = document.createElement("canvas");
				canvas.width = width;
				canvas.height = height;
				const ctx = canvas.getContext("2d");
				if (!ctx) {
					resolve(null);
					return;
				}
				ctx.fillStyle = "#ffffff";
				ctx.fillRect(0, 0, width, height);
				ctx.drawImage(img, 0, 0, width, height);
				resolve(canvas.toDataURL("image/png"));
			} catch {
				resolve(null);
			}
		};
		img.onerror = () => resolve(null);
		img.src = dataUrl;
	});
}

/**
 * Normalize composer files for the chat API: PNG/JPEG/GIF/WebP stay images;
 * SVG is rasterized to PNG when possible.
 */
export async function normalizeChatFiles(
	files: Array<{
		mediaType?: string;
		url?: string;
		filename?: string;
	}>,
): Promise<ChatFilePart[]> {
	const out: ChatFilePart[] = [];
	for (const file of files) {
		if (typeof file.url !== "string" || !file.url.startsWith("data:")) continue;
		const fromUrl = mediaTypeFromDataUrl(file.url);
		const mediaType = (file.mediaType || fromUrl || "application/octet-stream").toLowerCase();
		const filename = file.filename;

		if (isSvg(mediaType, filename)) {
			const png = await svgDataUrlToPng(file.url);
			if (png) {
				out.push({
					mediaType: "image/png",
					url: png,
					filename: filename?.replace(/\.svg$/i, ".png") ?? "image.png",
				});
				continue;
			}
			// Fall through as svg+xml — server will inline XML as text.
			out.push({
				mediaType: "image/svg+xml",
				url: file.url,
				filename,
			});
			continue;
		}

		if (mediaType.startsWith("image/") && !VISION_TYPES.has(mediaType)) {
			// Skip exotic image types rather than 400 from Anthropic.
			continue;
		}

		out.push({
			mediaType: mediaType === "image/jpg" ? "image/jpeg" : mediaType,
			url: file.url,
			filename,
		});
	}
	return out;
}
